import { isBooleanTrue, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { createWithId, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter } from 'firebase/firestore';
import { db, loadStorage } from '../firebase';
import { v4 as uuid } from 'uuid';
import { toLower } from "lodash";

class Affiliate {
    static collectionName = 'affiliates';

    id = null;
    name = null;
    payoutMethod = null; // PayoutMethods CASH or CREDIT
    payoutAmount = 0; // amount to be paid out per conversion via the payoutMethod
    #file = null;
    w9FileRefId = null; // their W9 file on record
    userId = null; // if associated with a user account (customer)
    conversionTotal = 0; // total of conversion value for all conversions based on payoutAmount
    conversionCount = 0; // total number of conversions
    salesTotal = 0; // total of sales value for all conversions based on order totals

    constructor(affiliate) {
        if (isObjectNotEmpty(affiliate)) {
            if (isStringNotEmpty(affiliate.id)) { this.id = toLower(affiliate.id); }
            if (isStringNotEmpty(affiliate.name)) { this.name = affiliate.name; }
            if (isStringNotEmpty(affiliate.payoutMethod)) { this.payoutMethod = affiliate.payoutMethod; }
            if (isNumberGreaterThanOrEqualToZero(affiliate.payoutAmount)) { this.payoutAmount = affiliate.payoutAmount; }

            if (isNotNullOrUndefined(affiliate.file)) { this.#file = affiliate.file; }
            if (isStringNotEmpty(affiliate.w9FileRefId)) { this.w9FileRefId = affiliate.w9FileRefId; }

            if (isStringNotEmpty(affiliate.userId)) { this.userId = affiliate.userId; }
            if (isNumberGreaterThanOrEqualToZero(affiliate.conversionTotal)) { this.conversionTotal = affiliate.conversionTotal; }
            if (isNumberGreaterThanOrEqualToZero(affiliate.conversionCount)) { this.conversionCount = affiliate.conversionCount; }
            if (isNumberGreaterThanOrEqualToZero(affiliate.salesTotal)) { this.salesTotal = affiliate.salesTotal; }
        }
    }

    toDto = () => {
        return {
            id: this.id,
            name: this.name,
            payoutMethod: this.payoutMethod,
            payoutAmount: this.payoutAmount,
            w9FileRefId: this.w9FileRefId,
            userId: this.userId,
            conversionTotal: this.conversionTotal,
            conversionCount: this.conversionCount,
            salesTotal: this.salesTotal
        };
    }

    isValid = () => {
        if (isStringEmpty(this.id)) {
            throw new Error('Affiliate id is required.');
        }
        if (isStringEmpty(this.name)) {
            throw new Error('Affiliate name is required.');
        }

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + this.getFileExt(file);
                // console.log('fileName', fileName);
                const { storage, ref, uploadBytes } = await loadStorage();
                const storageRef = ref(storage, 'affiliates/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);


        return toLower(ext);
    }

    uploadFile = async () => {
        if (isNotNullOrUndefined(this.#file)) {
            const uploadRes = await Affiliate.addFile(this.#file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                this.w9FileRefId = uploadRes.ref.fullPath;
            }
        }
    }

    create = async () => {
        if (this.isValid()) {
            await this.uploadFile();

            return await createWithId(Affiliate.collectionName, this.id, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Affiliate.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await this.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                return await this.updateField(id, 'w9FileRefId', uploadRes.ref.fullPath);
            }
        }

        return false;
    }

    static update = async (id, updatedAffiliate, oldObj) => {
        const updatedObj = new Affiliate(updatedAffiliate);
        if (updatedObj.isValid() && id === updatedAffiliate.id) {
            if (isNotNullOrUndefined(updatedObj.#file)) {
                const uploadRes = await Affiliate.addFile(updatedObj.#file);
                // console.log(uploadRes);
                if (isObjectNotEmpty(uploadRes)) {
                    updatedObj.w9FileRefId = uploadRes.ref.fullPath;
                }
            }

            const success = await update(Affiliate.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj };
            }
        } else {
            throw new Error('id and id field of the updated affiliate must match.')
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Affiliate.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        return await getById(Affiliate.collectionName, id);
    }

    static getAll = async () => {
        return await getAll(Affiliate.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Affiliate.collectionName, fieldName, fieldValue, comparer);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Affiliate.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { } = queryParameters;

        let queryConstraints = [];

        let q = query(collection(db, Affiliate.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'name') => {
        const { } = queryParameters;

        let queryConstraints = [];

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Affiliate.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Affiliate.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Affiliate.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'name');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Affiliate;